/**
 * E2EE Session Manager
 *
 * High-level API that combines X3DH and Double Ratchet into
 * a simple encrypt/decrypt interface per conversation.
 *
 * Usage:
 *   // Initiator (Alice)
 *   const session = createSession(aliceKeys, bobBundle);
 *   const encrypted = session.encrypt("Hello Bob!");
 *
 *   // Recipient (Bob)
 *   const session = receiveSession(bobKeys, message);
 *   const plaintext = session.decrypt(encrypted);
 */

import { x3dhSender, x3dhRecipient } from "./x3dh.js";
import type { PreKeyBundle, X3DHSenderResult } from "./x3dh.js";
import {
  initSender,
  initRecipient,
  ratchetEncrypt,
  ratchetDecrypt,
} from "./double-ratchet.js";
import type { RatchetState, EncryptedMessage } from "./double-ratchet.js";
import type { IdentityKeyPair, KeyPair } from "./keys.js";
import { toBase64, fromBase64 } from "./keys.js";

// ============================================================
// Types
// ============================================================

export interface CryptoSession {
  /** Encrypt a plaintext string, returns base64-encoded encrypted message */
  encrypt(plaintext: string): string;

  /** Decrypt a base64-encoded encrypted message, returns plaintext string */
  decrypt(ciphertext: string): string;

  /** Export session state for persistence (encrypted with a provided key) */
  exportState(): SerializedRatchetState;

  /** Session metadata */
  remoteIdentityKey: Uint8Array;
}

export interface SerializedRatchetState {
  dhSendingPrivate: string;
  dhSendingPublic: string;
  dhReceivingKey: string | null;
  rootKey: string;
  sendingChainKey: string | null;
  receivingChainKey: string | null;
  sendingMessageNumber: number;
  receivingMessageNumber: number;
  previousSendingChainLength: number;
  skippedKeys: Array<[string, string]>;
}

interface InitialMessage {
  type: "initial";
  senderIdentityKey: string; // base64
  ephemeralKey: string; // base64
  usedSignedPreKeyId: number;
  usedPreKeyId?: number;
  // F22b D2: first 16 chars of the recipient SPK public key (base64) the
  // sender fetched from the server.  Backward-compat — receivers that do not
  // recognise this field silently ignore it; receivers that do can compare
  // against their local SPK pub and detect server↔IDB drift before attempting
  // the full X3DH derivation (which would fail with "invalid ghash tag").
  usedSignedPreKeyPubSnippet?: string;
  message: SerializedEncryptedMessage;
}

interface SerializedEncryptedMessage {
  dhPublicKey: string; // base64
  previousChainLength: number;
  messageNumber: number;
  ciphertext: string; // base64
  nonce: string; // base64
}

// ============================================================
// Session Creation
// ============================================================

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/**
 * Create a new E2EE session as initiator (sender of first message)
 *
 * The first call to encrypt() on the returned session will produce an
 * "initial" message containing the X3DH handshake data. Subsequent
 * encrypt() calls produce regular ratchet messages.
 */
export function createSession(
  senderIdentity: IdentityKeyPair,
  recipientBundle: PreKeyBundle,
): CryptoSession {
  // Perform X3DH
  const x3dhResult = x3dhSender(senderIdentity, recipientBundle);

  // Initialize Double Ratchet
  const state = initSender(
    x3dhResult.sharedSecret,
    recipientBundle.signedPreKey.publicKey,
  );

  return makeInitiatorSession(state, x3dhResult, senderIdentity, recipientBundle);
}

/**
 * Receive and process an initial message to create a session (recipient)
 */
export function receiveSession(
  recipientIdentity: IdentityKeyPair,
  recipientSignedPreKey: KeyPair,
  recipientPreKey: KeyPair | null,
  initialMessageBase64: string,
): { session: CryptoSession; plaintext: string } {
  const initialMessage = JSON.parse(
    textDecoder.decode(fromBase64(initialMessageBase64)),
  ) as InitialMessage;

  if (initialMessage.type !== "initial") {
    throw new Error("Expected initial message for session creation");
  }

  const senderIdentityKey = fromBase64(initialMessage.senderIdentityKey);
  const senderEphemeralKey = fromBase64(initialMessage.ephemeralKey);



  // Perform X3DH (recipient side)
  const x3dhResult = x3dhRecipient(
    recipientIdentity,
    recipientSignedPreKey,
    recipientPreKey,
    senderIdentityKey,
    senderEphemeralKey,
  );



  // Initialize Double Ratchet (recipient)
  const state = initRecipient(x3dhResult.sharedSecret, recipientSignedPreKey);

  const session = makeSession(state, x3dhResult.associatedData, senderIdentityKey);

  // Decrypt the initial message
  const encMsg = deserializeEncryptedMessage(initialMessage.message);



  const plaintext = ratchetDecrypt(state, encMsg, x3dhResult.associatedData);

  return {
    session,
    plaintext: textDecoder.decode(plaintext),
  };
}

/**
 * Restore a session from serialized state
 */
export function restoreSession(
  serialized: SerializedRatchetState,
  associatedData: Uint8Array,
  remoteIdentityKey: Uint8Array,
): CryptoSession {
  const state = deserializeState(serialized);
  return makeSession(state, associatedData, remoteIdentityKey);
}

// ============================================================
// Internal: Session Implementation
// ============================================================

/**
 * Initiator session: first encrypt() produces an "initial" message with X3DH data,
 * subsequent calls produce regular ratchet messages.
 */
function makeInitiatorSession(
  state: RatchetState,
  x3dhResult: X3DHSenderResult,
  senderIdentity: IdentityKeyPair,
  recipientBundle: PreKeyBundle,
): CryptoSession {
  let isFirstMessage = true;

  return {
    remoteIdentityKey: recipientBundle.identityKey,

    encrypt(plaintext: string): string {
      const plaintextBytes = textEncoder.encode(plaintext);
      const encrypted = ratchetEncrypt(state, plaintextBytes, x3dhResult.associatedData);
      const serializedMsg = serializeEncryptedMessage(encrypted);

      if (isFirstMessage) {
        isFirstMessage = false;
        // Wrap in initial message with X3DH data
        const initialMessage: InitialMessage = {
          type: "initial",
          senderIdentityKey: toBase64(senderIdentity.curve25519.publicKey),
          ephemeralKey: toBase64(x3dhResult.ephemeralPublicKey),
          usedSignedPreKeyId: x3dhResult.usedSignedPreKeyId,
          usedPreKeyId: x3dhResult.usedPreKeyId,
          // F22b D2: embed the first 16 chars of the recipient SPK pub that
          // the sender used when building this bundle.  The recipient can
          // compare against its own localSPKPub snippet to detect drift
          // before the costly (and doomed) X3DH derivation is attempted.
          usedSignedPreKeyPubSnippet: toBase64(recipientBundle.signedPreKey.publicKey).slice(0, 16),
          message: serializedMsg,
        };
        return toBase64(textEncoder.encode(JSON.stringify(initialMessage)));
      }

      return toBase64(textEncoder.encode(JSON.stringify(serializedMsg)));
    },

    decrypt(ciphertext: string): string {
      const parsed = JSON.parse(
        textDecoder.decode(fromBase64(ciphertext)),
      ) as SerializedEncryptedMessage;
      const encrypted = deserializeEncryptedMessage(parsed);
      const decrypted = ratchetDecrypt(state, encrypted, x3dhResult.associatedData);
      return textDecoder.decode(decrypted);
    },

    exportState(): SerializedRatchetState {
      return serializeState(state);
    },
  };
}

function makeSession(
  state: RatchetState,
  associatedData: Uint8Array,
  remoteIdentityKey: Uint8Array,
): CryptoSession {
  return {
    remoteIdentityKey,

    encrypt(plaintext: string): string {
      const plaintextBytes = textEncoder.encode(plaintext);
      const encrypted = ratchetEncrypt(state, plaintextBytes, associatedData);
      return toBase64(
        textEncoder.encode(JSON.stringify(serializeEncryptedMessage(encrypted))),
      );
    },

    decrypt(ciphertext: string): string {
      const parsed = JSON.parse(
        textDecoder.decode(fromBase64(ciphertext)),
      ) as SerializedEncryptedMessage;
      const encrypted = deserializeEncryptedMessage(parsed);
      const decrypted = ratchetDecrypt(state, encrypted, associatedData);
      return textDecoder.decode(decrypted);
    },

    exportState(): SerializedRatchetState {
      return serializeState(state);
    },
  };
}

// ============================================================
// Serialization
// ============================================================

function serializeEncryptedMessage(msg: EncryptedMessage): SerializedEncryptedMessage {
  return {
    dhPublicKey: toBase64(msg.header.dhPublicKey),
    previousChainLength: msg.header.previousChainLength,
    messageNumber: msg.header.messageNumber,
    ciphertext: toBase64(msg.ciphertext),
    nonce: toBase64(msg.nonce),
  };
}

function deserializeEncryptedMessage(msg: SerializedEncryptedMessage): EncryptedMessage {
  return {
    header: {
      dhPublicKey: fromBase64(msg.dhPublicKey),
      previousChainLength: msg.previousChainLength,
      messageNumber: msg.messageNumber,
    },
    ciphertext: fromBase64(msg.ciphertext),
    nonce: fromBase64(msg.nonce),
    tag: new Uint8Array(0), // Tag is embedded in ciphertext for GCM
  };
}

function serializeState(state: RatchetState): SerializedRatchetState {
  const skippedEntries: Array<[string, string]> = [];
  for (const [key, value] of state.skippedKeys) {
    skippedEntries.push([key, toBase64(value)]);
  }

  return {
    dhSendingPrivate: toBase64(state.dhSendingKeyPair.privateKey),
    dhSendingPublic: toBase64(state.dhSendingKeyPair.publicKey),
    dhReceivingKey: state.dhReceivingKey ? toBase64(state.dhReceivingKey) : null,
    rootKey: toBase64(state.rootKey),
    sendingChainKey: state.sendingChainKey ? toBase64(state.sendingChainKey) : null,
    receivingChainKey: state.receivingChainKey ? toBase64(state.receivingChainKey) : null,
    sendingMessageNumber: state.sendingMessageNumber,
    receivingMessageNumber: state.receivingMessageNumber,
    previousSendingChainLength: state.previousSendingChainLength,
    skippedKeys: skippedEntries,
  };
}

function deserializeState(s: SerializedRatchetState): RatchetState {
  const skippedKeys = new Map<string, Uint8Array>();
  for (const [key, value] of s.skippedKeys) {
    skippedKeys.set(key, fromBase64(value));
  }

  return {
    dhSendingKeyPair: {
      privateKey: fromBase64(s.dhSendingPrivate),
      publicKey: fromBase64(s.dhSendingPublic),
    },
    dhReceivingKey: s.dhReceivingKey ? fromBase64(s.dhReceivingKey) : null,
    rootKey: fromBase64(s.rootKey),
    sendingChainKey: s.sendingChainKey ? fromBase64(s.sendingChainKey) : null,
    receivingChainKey: s.receivingChainKey ? fromBase64(s.receivingChainKey) : null,
    sendingMessageNumber: s.sendingMessageNumber,
    receivingMessageNumber: s.receivingMessageNumber,
    previousSendingChainLength: s.previousSendingChainLength,
    skippedKeys,
  };
}
